import {Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, Subscription, of} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {VariableCategoryModel, VariableModel} from "../models/variable-category.model";
import {SimpleNode} from "../models/simple-node.model";

@Injectable({
  providedIn: 'root'
})
export class VariablesCategoriesService {

  private url = `${environment.apiURL}/variables-categories`;
  variablesCategoriesSubject = new BehaviorSubject<SimpleNode[]>([]);
  variablesCategories: VariableCategoryModel[];
  treeData: any[];
  variablesCategoriesSubscription: Subscription;

  constructor(
    private http: HttpClient
  ) {
  }

  public getVariablesCategories(isResearcher = false, category = null): void {
    let url = '';
    if (isResearcher) {
      url = `${this.url}?public=false`;
    } else {
      url = this.url;
    }
    if (category) {
      url += url.includes('?') ? '&' : '?';
      url += `category=${category}`;
    }

    this.variablesCategoriesSubscription = this.http.get(url).subscribe((variablesCategories: VariableCategoryModel[]) => {
      this.variablesCategories = variablesCategories;
      this.treeData = this.transformVariableCategoriesToTreeData();
      const data = this.treeData;
      this.variablesCategoriesSubject.next(data);
    })
  }

  /**
   * #24 — the technical identifiers a dataset's variables are explicitly enabled for
   * graphing under.
   *
   * The authoritative predicate lives in Admin: a variable is chart-enabled when it is in
   * the category entry's `columns` and *not* in its `disabled_columns`. The API applies
   * exactly that subtraction and returns the result as `columnKeys` (#23), so this method
   * reads the existing authenticated `variables-categories` contract rather than adding an
   * API surface of its own. `/product-page/:collectionId` is behind `AuthGuardService` and
   * every request carries the bearer token, so `?public=false` returns the researcher
   * configuration — the same unscoped read the graph page performs on every load.
   *
   * Stateless on purpose: unlike `getVariablesCategories()` it neither stores the response
   * nor pushes into `variablesCategoriesSubject`/`treeData`, which are shared state owned
   * by the graph-page tree.
   *
   * Resolved by `collectionId` alone, across every `public:false` category document.
   * `collection.category` is not consulted: no Admin path writes it (every current MED
   * collection has `null`), and nothing constrains it to name the category document a
   * collection is actually configured under — the configuration is keyed by
   * `variables[].collectionId`, so the id is the only authoritative handle.
   *
   * Fails safe (issue rule 5): a request error, a non-array body, no `variables[]` entry
   * for this collection, or an absent/empty `columnKeys` — including an API deployment
   * that predates #23 — all yield `[]`, which renders an empty selector. So does an
   * *ambiguous* configuration: the same `collectionId` appearing in more than one
   * `public:false` document (or twice in one) is invalid configuration, and picking either
   * entry would make eligibility depend on response ordering — exactly one match is
   * required. There is deliberately no fallback to `columns`: those are display labels,
   * and mixing them with technical keys is exactly the confusion #23 removes.
   */
  public getChartEnabledColumnKeys(collectionId: string): Observable<string[]> {
    if (!collectionId) {
      return of([]);
    }

    const url = `${this.url}?public=false`;

    return this.http.get<VariableCategoryModel[]>(url).pipe(
      map(categories => VariablesCategoriesService.extractChartEnabledColumnKeys(categories, collectionId)),
      catchError(() => of([]))
    );
  }

  /**
   * Pick the `columnKeys` of the single `variables[]` entry belonging to `collectionId`.
   *
   * Matching on the id rather than the collection name because the id is what Admin keys
   * every category update by, and it survives a collection title change.
   *
   * The whole response is scanned and exactly one match is required: zero matches is an
   * unconfigured collection, more than one is ambiguous configuration, and both fail
   * closed to `[]` rather than letting response ordering decide which entry wins.
   */
  private static extractChartEnabledColumnKeys(categories: VariableCategoryModel[], collectionId: string): string[] {
    if (!Array.isArray(categories)) {
      return [];
    }

    const wanted = String(collectionId);
    const matches: VariableModel[] = [];

    for (const category of categories) {
      const variables: VariableModel[] = Array.isArray(category?.variables) ? category.variables : [];
      for (const variable of variables) {
        if (variable && String(variable.collectionId ?? '') === wanted) {
          matches.push(variable);
        }
      }
    }

    if (matches.length !== 1) {
      return [];
    }

    return Array.isArray(matches[0].columnKeys)
      ? matches[0].columnKeys.filter(key => typeof key === 'string' && key !== '')
      : [];
  }

  private transformVariableCategoriesToTreeData(): SimpleNode[] {
    const treeData = [];
    let secondChildren = [];
    let thirdChildren = [];

    this.variablesCategories.map(item => {
      secondChildren = item.variables.map(collection => {
        // #23: prefer the technical identifiers, so the value carried through the form,
        // localStorage and the /graph request is the storage/query key rather than a
        // display label. `columnKeys` is added additively by the API — until that change is
        // deployed it is absent and we fall back to `columns` (today's behaviour, where the
        // value already is the public title). Labels are resolved at render time either
        // way: a lookup miss returns the input unchanged, so nothing is mutated.
        const useKeys = !!collection.columnKeys?.length;
        const columns = useKeys ? collection.columnKeys : collection.columns;

        thirdChildren = (columns || []).map((column, index) => {
          return {
            category: item.category,
            collectionName: collection.collectionName,
            collectionPublicName: collection.collectionPublicName,
            item: column,
            // Display label parallel to the technical key, so the type-ahead filter below
            // can still match what the user actually sees. `columns` and `columnKeys` are
            // index-aligned by the API; without `columnKeys` the two are the same value.
            itemTitle: useKeys ? (collection.columns?.[index] ?? column) : column,
          };
        })

        return {
          item: collection.collectionPublicName ? collection.collectionPublicName : collection.collectionName,
          is_med: (collection as any).collection?.is_med,
          is_kinneret: (collection as any).collection?.is_kinneret,
          children: thirdChildren,
        };
      })

      treeData.push({
        item: item.category,
        children: secondChildren
      });
    })
    return treeData;
  }

  public filter(filterText: string) {
    let filteredTreeData;
    if (filterText) {
      // Filter the tree
      function filter(array, text) {
        const getChildren = (result, object) => {
          // Match the technical key *or* the displayed label: once the node value is the
          // technical key (#23), searching for the configured public name would otherwise
          // stop matching.
          const haystack = [object.item, object.itemTitle]
            .filter(value => typeof value === 'string')
            .map(value => value.toLowerCase());

          if (haystack.some(value => value.indexOf(text.toLowerCase()) > -1)) {
            result.push(object);
            return result;
          }
          if (Array.isArray(object.children)) {
            const children = object.children.reduce(getChildren, []);
            if (children.length) result.push({...object, children});
          }
          return result;
        };

        return array.reduce(getChildren, []);
      }

      filteredTreeData = filter(this.treeData, filterText);
    } else {
      // Return the initial tree
      filteredTreeData = this.treeData;
    }

    // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
    // file node as children.
    const data = filteredTreeData;
    // Notify the change.
    this.variablesCategoriesSubject.next(data);
  }
}
